import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Container,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Cancel,
  School,
  Edit
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import Layout from '../Layout/Layout';
import CreateCourseForm from '../CreateCourse/Components/CreateCourseForm';
import axios from 'axios';

const EditCourseV2 = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCourse();
  }, [id]);

  const fetchCourse = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_SERVER}/api/courses/${id}`);
      setCourse(response.data);
    } catch (error) {
      console.error('Error fetching course:', error);
      setError('Failed to load course details');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/my-courses');
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading course details...</Typography>
          </Box>
        </Container>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ 
              background: 'linear-gradient(135deg, #712d94 0%, #5a2470 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a2470 0%, #712d94 100%)',
              }
            }}
          >
            Back to Courses
          </Button>
        </Container>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Course not found
          </Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ 
              background: 'linear-gradient(135deg, #712d94 0%, #5a2470 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a2470 0%, #712d94 100%)',
              }
            }}
          >
            Back to Courses
          </Button>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 3, 
        p: { xs: 1, sm: 2 },
        maxWidth: '100%'
      }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 0 }}>
          <Link
            color="inherit"
            href="/my-courses"
            onClick={(e) => {
              e.preventDefault();
              navigate('/my-courses');
            }}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              textDecoration: 'none',
              fontSize: '0.75rem',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            <School sx={{ mr: 0.5, fontSize: 14 }} />
            My Courses
          </Link>
          <Typography 
            color="text.primary" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              fontSize: '0.75rem'
            }}
          >
            <Edit sx={{ mr: 0.5, fontSize: 14 }} />
            Edit Course
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          mb: 3 
        }}>
          <IconButton
            onClick={() => navigate('/my-courses')}
            sx={{
              mr: 2,
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.main' + '10'
              }
            }}
          >
            <ArrowBack />
          </IconButton>
          
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Edit Course
          </Typography>
        </Box>

        {/* Course Form */}
        <CreateCourseForm courseId={id} />
      </Box>
    </Layout>
  );
};

export default EditCourseV2;
